import { ServiceProvider } from "../../dependencies_management/ServiceProvider";
import { GaEventData } from "../../types/GaEventData";
import { GoogleAnalyticsDimensionsAndMetricsProvider } from "../../utilities/analytics_parameters/GoogleAnalyticsDimentionsAndMetricsProvider";
import { DocumentWrapper } from "../../utilities/document_wrapping/DocumentWrapper";
import { AnalyticsPageNameProvider } from "../../utilities/page_name/AnalyticsPageNameProvider";
import { PageTypeProvider } from "../../utilities/page_type/PageTypeProvider";
import { AnalyticsTracker } from "../AnalyticsTracker";
import { getCookieByName } from "@ado/cookie-library";
import { GoogleAnalyticsDimensionsAndMetrics } from "../../utilities/analytics_parameters/GoogleAnalyticsDimentionsAndMetrics";
import { GoogleAnalyticsTrackerOptions } from "./GoogleAnalyticsTrackerOptions";
import { GlobalSiteTagDimensionsAndMetrics } from "./GlobalSiteTagDimensionsAndMetrics";
import { GlobalSiteTagRemarketingDimensionsAndMetrics } from "./GlobalSiteTagRemarketingDimensionsAndMetrics";
import { GOOGLE_ADS_GROUP } from "../../analytics_initialization/GoogleAnalyticsGroups";

export class GlobalSiteTagAnalyticsTracker extends AnalyticsTracker<GaEventData> {
	private readonly analyticsPageNameProvider: AnalyticsPageNameProvider;
	private readonly document: DocumentWrapper;
	private readonly pageTypeProvider: PageTypeProvider;
	private readonly dimensionsAndMetricsProvider: GoogleAnalyticsDimensionsAndMetricsProvider;

	constructor(
		private readonly options: GoogleAnalyticsTrackerOptions,
		readonly serviceProvider: ServiceProvider
	) {
		super();
		this.analyticsPageNameProvider =
			serviceProvider.getAnalyticsPageNameProvider();
		this.document = serviceProvider.getDocumentWrapper();
		this.pageTypeProvider = serviceProvider.getPageTypeProvider();
		this.dimensionsAndMetricsProvider =
			serviceProvider.getGoogleAnalyticsDimensionsAndMetricsProvider();
	}

	submitPageView(): void {
		this.submitRemarketingPageView();
		this.submitPageViewWithCustomDimensionsAndMetrics();
	}

	submitEvent(eventData: GaEventData): void {
		const { category, action, label = this.getAnalyticsPageName() } = eventData;
		const eventDimensionsAndMetrics = {
			...this.createGlobalSiteTagDimensionsAndMetrics(),
			event_label: label,
			event_category: category,
		};
		this.submitGlobalSiteTagEvent(action, eventDimensionsAndMetrics);
	}

	private submitRemarketingPageView(): void {
		const pageName = this.getAnalyticsPageName();
		const pageType = this.pageTypeProvider.getPageTypeByName(pageName);
		const { productCode } = this.executionContext;
		const itemId =
			productCode ??
			pageName ??
			this.document.windowLocation.pathWithQueryString;
		const criteria = this.getCriteriaByProductCode(productCode);

		const remarketingDimensionsAndMetrics = {
			prodid: productCode,
			pagetype: pageType,
			pname: pageName,
			dynx_itemid: itemId,
			dynx_itemid2: criteria,
			dynx_pagetype: pageType,
			send_to: GOOGLE_ADS_GROUP,
		};
		this.submitGlobalSiteTagPageView(remarketingDimensionsAndMetrics);
	}

	private submitPageViewWithCustomDimensionsAndMetrics(): void {
		this.submitGlobalSiteTagPageView(
			this.createGlobalSiteTagDimensionsAndMetrics()
		);
	}

	private submitGlobalSiteTagPageView(
		dimensionsAndMetrics:
			| GlobalSiteTagRemarketingDimensionsAndMetrics
			| GlobalSiteTagDimensionsAndMetrics
	) {
		this.submitGlobalSiteTagEvent("page_view", dimensionsAndMetrics);
	}

	private submitGlobalSiteTagEvent(
		action: string | undefined,
		dimensionsAndMetrics:
			| GlobalSiteTagRemarketingDimensionsAndMetrics
			| GlobalSiteTagDimensionsAndMetrics
	) {
		this.options.googleAnalyticsFunctions.gtag(
			"event",
			action,
			dimensionsAndMetrics
		);
	}

	private getAnalyticsPageName(): string | undefined {
		return this.analyticsPageNameProvider.providePageName(
			this.executionContext
		);
	}

	private getCriteriaByProductCode(productCode: string | undefined): string {
		const ampersandSeparatedCriteriaByProductCodeValues =
			getCookieByName("ldcn");

		if (
			ampersandSeparatedCriteriaByProductCodeValues === undefined ||
			productCode === undefined ||
			productCode.length === 0
		) {
			return "";
		}

		const criteriaByProductCodeValues =
			ampersandSeparatedCriteriaByProductCodeValues.split("&");
		for (const criteriaByProductCode of criteriaByProductCodeValues) {
			if (
				criteriaByProductCode.includes(productCode) &&
				criteriaByProductCode.includes("=")
			) {
				return criteriaByProductCode.substring(
					criteriaByProductCode.indexOf("=") + 1
				);
			}
		}

		return "";
	}

	private getCustomDimensionsAndMetrics(): GoogleAnalyticsDimensionsAndMetrics {
		return this.dimensionsAndMetricsProvider.provideDimensionsAndMetrics(
			this.executionContext,
			this.options.isUserLicensed
		);
	}

	private createGlobalSiteTagDimensionsAndMetrics(): GlobalSiteTagDimensionsAndMetrics {
		const {
			semicolonSeparatedExperiments,
			location,
			file,
			doNotTrack,
			isUserLicensed,
			pageTitle,
			hasLoginIdIssue,
		} = this.getCustomDimensionsAndMetrics();

		return {
			exp: semicolonSeparatedExperiments,
			loc: location,
			file: file,
			licensed: isUserLicensed,
			productCode: this.executionContext.productCode,
			doNotTrack: doNotTrack,
			LIDIssue: hasLoginIdIssue,
			titleTag: document.title,
			page_title: pageTitle,
		};
	}
}
